import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  contentChild,
  Directive,
  HostBinding,
  inject,
  InjectionToken,
  input,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { GALAXY_LIST_CONTAINER_TOKEN } from './list-container.component';
import { Spacer } from './list.config';

/**
 * A placeholder used to define an empty void in the icon slot. Allows a parent container to
 * conditionally offset all child items by a given width. Outside this use case, it is expected
 * that developers will simply use css to offset the content as needed.
 */
export const ICON_PLACEHOLDER = new InjectionToken<Spacer>('GalaxyListItemIconPlaceholder');

abstract class GalaxyListItemIcon {}
@Directive({
  selector: '[glxyListItemIcon]',
  providers: [{ provide: GalaxyListItemIcon, useExisting: GalaxyListItemIconDirective }],

})
export class GalaxyListItemIconDirective {}

@Component({
  selector: 'glxy-list-item',
  templateUrl: './list-item.component.html',
  styleUrls: ['./list-item.component.scss'],
  providers: [{ provide: ICON_PLACEHOLDER, useValue: undefined }],
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.glxy-list-item-default]': 'designVersion() === "default" || !designVersion()',
    '[class.glxy-list-item-legacy]': 'designVersion() === "legacy"',
  },
})
export class GalaxyListItemComponent {
  protected designVersion = inject(GALAXY_LIST_CONTAINER_TOKEN, { optional: true })?.designVersion ?? signal('default');
  @HostBinding('class') class = 'glxy-list-item';
  protected readonly iconSpacerSize = inject(ICON_PLACEHOLDER, { optional: true, skipSelf: true });
  /**
   * Whether to render a placeholder icon. This is used to offset the content when no icon is
   * present, but the item is nested in a container that does have an icon.
   */
  protected readonly iconSpacerEnabled = input(true);

  private readonly iconContent = contentChild<GalaxyListItemIcon | null>(GalaxyListItemIcon);
  protected readonly hasIcon = computed(() => !!this.iconContent());
}
